import { PublicClientApplication } from '@azure/msal-browser'

import { msalConfig } from './config'

/**
 * MSAL v3 must be initialised and must settle any redirect before React
 * renders, so this runs during bootstrap in main.tsx.
 */
export async function createMsalInstance(): Promise<PublicClientApplication> {
  const instance = new PublicClientApplication(msalConfig)
  await instance.initialize()

  const result = await instance.handleRedirectPromise()
  if (result?.account) {
    instance.setActiveAccount(result.account)
  } else {
    const [account] = instance.getAllAccounts()
    if (account) instance.setActiveAccount(account)
  }

  clearAuthResponseFromUrl()
  return instance
}

/**
 * Take the sign-in response out of the address bar and out of history.
 *
 * Entra returns the authorisation code in the URL fragment, so after a sign in
 * the address bar holds `#code=...`. Left there it is the entry the browser goes
 * back to, which is what produced the "Confirm Form Resubmission" and
 * ERR_CACHE_MISS page QA saw. `replaceState` rewrites the current entry rather
 * than adding one, so the response is not somewhere Back can reach.
 *
 * It also keeps a code and a token fragment out of anything that reads the
 * address bar, which is worth having on its own.
 *
 * Only ever runs after `handleRedirectPromise` has resolved, so MSAL has already
 * taken what it needs from the fragment.
 */
function clearAuthResponseFromUrl(): void {
  const { hash, search, pathname } = window.location
  const looksLikeAuthResponse =
    /[#&](code|id_token|access_token|state|error)=/.test(hash) ||
    /[?&](code|error)=/.test(search)
  if (!looksLikeAuthResponse) return
  window.history.replaceState(null, '', pathname)
}
